<template>
    <v-dialog v-model="isActive" scrollable>
        <template v-slot:activator="{ props: activatorProps }">
            <slot name="activator" :props="activatorProps">
                <v-btn
                    v-bind="activatorProps"
                    color="surface-variant"
                    text="Neuen Nutzer anlegen"
                    variant="flat"
                />
            </slot>
        </template>

        <template #default>
            <v-card>
                <v-card-title> {{ title }} </v-card-title>
                <v-card-subtitle>
                    Die Rolle erhält der Nutzer sobald er den Modulverantwortlichen bzw. Lehrenden
                    eines Moduls hinzugefügt wird.
                </v-card-subtitle>
                <v-card-text>
                    <div class="d-flex flex-column">
                        <div class="d-flex flex-column">
                            <div class="d-flex flex-nowrap">
                                <input-header title="Titel" />
                                <text-field
                                    v-model="editUser.titel"
                                    :is-loading="isLoading"
                                    autocomplete="off"
                                    placeholder="Titel"
                                    class=""
                                />
                            </div>
                            <div class="d-flex flex-nowrap">
                                <input-header title="Vorname" />
                                <text-field
                                    v-model="editUser.vorname"
                                    :is-loading="isLoading"
                                    autocomplete="off"
                                    placeholder="Vorname"
                                    class=""
                                />
                            </div>
                            <div class="d-flex flex-nowrap">
                                <input-header title="Nachname" />
                                <text-field
                                    v-model="editUser.nachname"
                                    :is-loading="isLoading"
                                    class=""
                                    autocomplete="off"
                                    placeholder="Nachname"
                                />
                            </div>

                            <div class="d-flex flex-nowrap">
                                <input-header title="E-Mail" />
                                <text-field
                                    v-model="editUser.email"
                                    :is-loading="isLoading"
                                    type="email"
                                    autocomplete="off"
                                    placeholder="E-Mail"
                                    class=""
                                />
                            </div>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="mr-4">
                    <v-btn
                        type="reset"
                        color="primary"
                        variant="outlined"
                        :disabled="isLoading"
                        @click="resetEditUser()"
                    >
                        Reset
                    </v-btn>
                    <v-btn
                        variant="tonal"
                        :loading="isLoading"
                        color="primary"
                        @click="handleCreateUser()"
                    >
                        Anlegen
                    </v-btn>
                </v-card-actions>
                <div v-show="userId" v-if="props.withRole">
                    <v-divider></v-divider>
                    <user-rollen v-if="userId" :user-id="userId" />
                </div>
            </v-card>
        </template>
    </v-dialog>
</template>

<script lang="ts" setup>
    import { Ref, watch } from 'vue'
    import { useAlertStore, useUserStore } from '@/js/stores'
    import { computed, onMounted, ref } from 'vue'
    import InputHeader from '@/js/components/Common/InputHeader.vue'
    import { TextField } from '@/js/components/Common'
    import { SignUpData, User } from '@/js/model'
    import UserRollen from '@/js/components/Benutzer/UserRollen.vue'

    const props = defineProps({
        withRole: {
            type: Boolean,
            required: false,
            default: () => true
        }
    })

    const alertStore = useAlertStore()
    const userStore = useUserStore()
    const editUser = ref({
        titel: '',
        vorname: '',
        nachname: '',
        email: ''
    })
    const userId = ref<string | undefined>(undefined)
    const isActive = ref(false) as Ref<boolean>

    const isLoading = computed(() => userStore.isLoading)
    const title = computed(() => {
        return `Neuen Benutzer anlegen.`
    })

    const resetEditUser = function () {
        editUser.value['titel'] = ''
        editUser.value['vorname'] = ''
        editUser.value['nachname'] = ''
        editUser.value['email'] = ''
        userId.value = undefined
    }

    onMounted(() => {
        resetEditUser()
    })

    watch(isActive, (open) => {
        if (open) {
            resetEditUser()
        }
    })

    const handleCreateUser = async () => {
        const signUpdata: SignUpData = { ...editUser.value }
        await userStore.createUser(signUpdata)
        if (!userStore.hasErrors) {
            alertStore.success(`Nutzer ${signUpdata.email} erfolgreich angelegt.`, 'Info')
            const user: User | undefined = userStore.getUserByEmail(signUpdata.email)
            userId.value = user ? user.userId : undefined
        } else {
            alertStore.error('Fehler beim Anlegen des Nutzers.', 'Fehler!')
        }

        if (!props.withRole) {
            isActive.value = false
        }
    }
</script>

<style lang="scss"></style>
